'use client';

import Icon from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { App, Popover, message } from 'antd';
import classNames from 'classnames';
import { useCallback } from 'react';

import DeleteSvg from '@/components/Icon/svgs/delete.svg';
import MoreSvg from '@/components/Icon/svgs/more.svg';
import { useTranslation } from '@/i18n/client';
import type { IChatListItem } from '@/service/note';
import { deleteTask } from '@/service/task';
import useNoteItemStore from '@/store/useNoteItemStore';
import { EVENT } from '@/utils/event';

import styles from './index.less';

interface IProps {
  className?: string;
  detail: IChatListItem;
  mindId?: string;
  onDeleteCb?: () => void;
  onEditCb?: () => void;
  [key: string]: any;
}

const ChatNoteSettings = (props: IProps) => {
  const { detail, onDeleteCb, mindId, className } = props;
  const { modal } = App.useApp();
  const addDeleteItemId = useNoteItemStore((state) => state.addDeleteItemId);
  const { t } = useTranslation();

  const { run: runDelete } = useRequest(deleteTask, {
    manual: true,
    onSuccess: (res) => {
      if (res.data.code === 0) {
        message.success(t('common.deleteSuccess'));
        dispatchEvent(new Event(EVENT.CLOSE_TASK_LIST));
        dispatchEvent(new CustomEvent(EVENT.DELETE_SESSION_CB, { detail: detail.sessionId }));
        detail.id && addDeleteItemId(Number(detail.id));
        onDeleteCb?.();
      } else {
        message.error(res.data.message || t('common.deleteFail'));
      }
    },
    onError: (err) => {
      message.error(err.message || t('common.deleteFail'));
    }
  });

  const renderPopoverContent = useCallback(() => {
    return (
      <div
        className={styles.popoverContentContainer}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          className={classNames(styles.itemContainer)}
          onClick={() => {
            modal.confirm({
              title: t('common.sureToDelete'),
              onOk: () => {
                if (!detail.sessionId || !mindId) {
                  message.error(t('error.oops'));

                  return;
                }

                runDelete({ mindId, sessionId: detail.sessionId });
              },
              okText: t('common.delete'),
              cancelText: t('common.cancel')
            });
          }}
        >
          <Icon className={styles.icon} component={DeleteSvg} />
          <span>{t('common.delete')}</span>
        </div>
      </div>
    );
  }, [detail, mindId, modal, runDelete]);

  return (
    <Popover
      arrow={false}
      content={renderPopoverContent()}
      overlayClassName={styles.overlayClassName}
      placement="bottomLeft"
      trigger={['hover']}
    >
      <Icon
        className={classNames(
          'w-6 h-6 p-[3px] text-primary-black text-base border border-solid box-border border-[#eee9e4] shadow-[0_4px_4px_0_rgba(0,0,0,0.08)] bg-primary-white rounded-[30px] ',
          className
        )}
        component={MoreSvg}
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
    </Popover>
  );
};

export default ChatNoteSettings;
